import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PART_USAGE_KIND,
  type CreatePartInput,
  type Part,
  type PartUsageKind,
} from '@etnrlz/shared';
import { lookupPart, useCreatePart, useParts, useUpdatePart } from '../../lib/queries';
import { Badge, Button, EmptyState, Field, Input, Loading, PageHeader, Select, cx } from '../../components/ui';
import { Modal } from '../../components/Modal';
import { PhotoUpload } from '../../components/PhotoUpload';
import { BarcodeScanner } from '../../components/BarcodeScanner';
import { Icon } from '../../components/icons';
import { formatMoney } from '../../lib/format';

const tipoLabel: Record<PartUsageKind, string> = { PRODUTO: 'Produto', PECA: 'Peça' };
const tipoColor: Record<PartUsageKind, string> = { PRODUTO: '#a855f7', PECA: '#3b82f6' };

/** Heurística: termo sem espaço = código de barras; com espaço = nome. */
const prefillFromQuery = (term: string): Partial<CreatePartInput> => {
  const t = term.trim();
  return /\s/.test(t) ? { nome: t } : { codigoBarras: t };
};

export function PartsPage({ touch }: { touch?: boolean }) {
  const [q, setQ] = useState('');
  const { data, isLoading } = useParts(q ? { q } : undefined);
  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<Partial<CreatePartInput>>({});
  const [editing, setEditing] = useState<Part | null>(null);
  const [newScanOpen, setNewScanOpen] = useState(false);
  const [searchScanOpen, setSearchScanOpen] = useState(false);

  const startCreate = (prefill: Partial<CreatePartInput>) => {
    setCreatePrefill(prefill);
    setCreateOpen(true);
  };

  // Busca por scan: acha o item; se não existir, já inicia o cadastro com o código.
  const onSearchScan = async (code: string) => {
    setSearchScanOpen(false);
    try {
      await lookupPart(code);
      setQ(code);
    } catch {
      startCreate({ codigoBarras: code });
    }
  };

  return (
    <div>
      <PageHeader
        title="Inventário"
        subtitle={data ? `${data.length} itens` : undefined}
        actions={
          <Button onClick={() => setNewScanOpen(true)} size={touch ? 'lg' : 'md'}>
            <Icon name="plus" className="h-4 w-4" /> Nova peça
          </Button>
        }
      />

      <div className="mb-4 flex gap-2 max-w-md">
        <Input
          placeholder="Buscar por nome, SKU, part number ou código…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            // Enter sem resultado (ex.: leitor USB digita + Enter) → já cadastra.
            if (e.key === 'Enter' && q.trim() && data && data.length === 0) {
              startCreate(prefillFromQuery(q));
            }
          }}
        />
        <Button variant="secondary" onClick={() => setSearchScanOpen(true)} title="Escanear código de barras">
          <Icon name="scan" className="h-4 w-4" />
        </Button>
      </div>

      {isLoading ? (
        <Loading />
      ) : !data || data.length === 0 ? (
        <EmptyState
          title={q ? `Nada encontrado para "${q}"` : 'Nenhuma peça'}
          hint={q ? 'Quer cadastrar este item?' : 'Cadastre itens do estoque.'}
          action={
            q ? (
              <Button onClick={() => startCreate(prefillFromQuery(q))}>
                <Icon name="plus" className="h-4 w-4" /> Cadastrar “{q}”
              </Button>
            ) : undefined
          }
        />
      ) : touch ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {data.map((p) => {
            const low = p.quantidade < p.estoqueMinimo;
            const tipo = p.tipo ?? 'PECA';
            return (
              <div key={p.id} className={cx('card p-4', low && 'border-st-danger/40')}>
                <div className="flex items-start justify-between gap-2">
                  <Link to={`parts/${p.id}`} className="font-medium hover:text-accent">
                    {p.nome}
                  </Link>
                  <Badge color={tipoColor[tipo]}>{tipoLabel[tipo]}</Badge>
                </div>
                <div className="text-xs text-muted num mt-1">
                  {p.sku} · {p.localizacao ?? '—'}
                </div>
                <div className="num text-2xl font-bold mt-2">
                  {p.quantidade}
                  <span className="text-sm text-muted font-normal"> / mín {p.estoqueMinimo}</span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  <button onClick={() => setEditing(p)} className="text-xs text-muted hover:text-accent">
                    Editar
                  </button>
                  {low && <Badge color="#ef4444">Repor</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-muted text-xs uppercase border-b border-border">
              <tr>
                <th className="text-left font-medium px-4 py-3">Item</th>
                <th className="text-left font-medium px-4 py-3">Tipo</th>
                <th className="text-left font-medium px-4 py-3">SKU</th>
                <th className="text-left font-medium px-4 py-3">Local</th>
                <th className="text-right font-medium px-4 py-3">Qtd</th>
                <th className="text-right font-medium px-4 py-3">Mín</th>
                <th className="text-right font-medium px-4 py-3">Custo</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.map((p) => {
                const low = p.quantidade < p.estoqueMinimo;
                const tipo = p.tipo ?? 'PECA';
                return (
                  <tr key={p.id} className="border-b border-border/50 hover:bg-surface-2">
                    <td className="px-4 py-3">
                      <Link to={`parts/${p.id}`} className="font-medium hover:text-accent">
                        {p.nome}
                      </Link>
                      <div className="text-xs text-muted">{p.marca ?? ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge color={tipoColor[tipo]}>{tipoLabel[tipo]}</Badge>
                    </td>
                    <td className="px-4 py-3 num text-muted">{p.sku}</td>
                    <td className="px-4 py-3 text-muted">{p.localizacao ?? '—'}</td>
                    <td className={cx('px-4 py-3 text-right num font-semibold', low && 'text-st-danger')}>
                      {p.quantidade}
                    </td>
                    <td className="px-4 py-3 text-right num text-muted">{p.estoqueMinimo}</td>
                    <td className="px-4 py-3 text-right num text-muted">{formatMoney(p.custoUnitario)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {low && <Badge color="#ef4444">Repor</Badge>}
                      <button
                        onClick={() => setEditing(p)}
                        className="ml-2 text-xs text-muted hover:text-accent"
                      >
                        Editar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <CreatePartModal open={createOpen} prefill={createPrefill} onClose={() => setCreateOpen(false)} />
      {editing && <EditPartModal part={editing} onClose={() => setEditing(null)} />}

      {/* "Nova peça": escaneia primeiro; pode pular pra cadastrar sem código. */}
      <BarcodeScanner
        open={newScanOpen}
        onClose={() => setNewScanOpen(false)}
        onResult={(code) => {
          setNewScanOpen(false);
          startCreate({ codigoBarras: code });
        }}
        skipLabel="Cadastrar sem código"
        onSkip={() => {
          setNewScanOpen(false);
          startCreate({});
        }}
      />

      {/* Busca por scan: acha ou já inicia o cadastro. */}
      <BarcodeScanner open={searchScanOpen} onClose={() => setSearchScanOpen(false)} onResult={onSearchScan} />
    </div>
  );
}

type PartFormState = Partial<CreatePartInput>;

/** Campos compartilhados entre criar e editar peça (tipo, código de barras, foto). */
function PartFormFields({
  f,
  set,
}: {
  f: PartFormState;
  set: (patch: PartFormState) => void;
}) {
  const [scan, setScan] = useState(false);
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <Field label="Nome">
          <Input value={f.nome ?? ''} onChange={(e) => set({ nome: e.target.value })} />
        </Field>
      </div>
      <Field label="Tipo">
        <Select value={f.tipo ?? 'PECA'} onChange={(e) => set({ tipo: e.target.value as PartUsageKind })}>
          {PART_USAGE_KIND.map((t) => (
            <option key={t} value={t}>
              {tipoLabel[t]}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="SKU">
        <Input value={f.sku ?? ''} onChange={(e) => set({ sku: e.target.value })} />
      </Field>
      <Field label="Categoria">
        <Input value={f.categoria ?? ''} onChange={(e) => set({ categoria: e.target.value })} />
      </Field>
      <Field label="Marca">
        <Input value={f.marca ?? ''} onChange={(e) => set({ marca: e.target.value })} />
      </Field>
      <Field label="Part number">
        <Input value={f.partNumber ?? ''} onChange={(e) => set({ partNumber: e.target.value })} />
      </Field>
      <Field label="Código de barras">
        <div className="flex gap-2">
          <Input
            value={f.codigoBarras ?? ''}
            onChange={(e) => set({ codigoBarras: e.target.value })}
            placeholder="EAN / UPC"
          />
          <Button variant="secondary" onClick={() => setScan(true)} title="Escanear">
            <Icon name="scan" className="h-4 w-4" />
          </Button>
        </div>
      </Field>
      <Field label="Quantidade">
        <Input type="number" value={f.quantidade ?? 0} onChange={(e) => set({ quantidade: Number(e.target.value) })} />
      </Field>
      <Field label="Estoque mínimo">
        <Input
          type="number"
          value={f.estoqueMinimo ?? 0}
          onChange={(e) => set({ estoqueMinimo: Number(e.target.value) })}
        />
      </Field>
      <Field label="Localização">
        <Input value={f.localizacao ?? ''} onChange={(e) => set({ localizacao: e.target.value })} placeholder="Prateleira A-2" />
      </Field>
      <Field label="Custo unit. (R$)">
        <Input
          type="number"
          value={f.custoUnitario ?? ''}
          onChange={(e) => set({ custoUnitario: e.target.value ? Number(e.target.value) : undefined })}
        />
      </Field>
      <div className="col-span-2">
        <Field label="Foto">
          <PhotoUpload value={f.fotoUrl} onChange={(url) => set({ fotoUrl: url })} prefix="parts" />
        </Field>
      </div>
      <BarcodeScanner open={scan} onClose={() => setScan(false)} onResult={(code) => set({ codigoBarras: code })} />
    </div>
  );
}

function CreatePartModal({
  open,
  prefill,
  onClose,
}: {
  open: boolean;
  prefill?: Partial<CreatePartInput>;
  onClose: () => void;
}) {
  const create = useCreatePart();
  const base = (): PartFormState => ({ tipo: 'PECA', quantidade: 0, estoqueMinimo: 0, ...prefill });
  const [f, setF] = useState<PartFormState>(base);
  useEffect(() => {
    if (open) setF(base());
  }, [open]);
  const set = (patch: PartFormState) => setF((s) => ({ ...s, ...patch }));
  const submit = async () => {
    await create.mutateAsync(f as CreatePartInput);
    onClose();
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Nova peça / produto"
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!f.nome || !f.sku || create.isPending}>
            Cadastrar
          </Button>
        </>
      }
    >
      <PartFormFields f={f} set={set} />
    </Modal>
  );
}

function EditPartModal({ part, onClose }: { part: Part; onClose: () => void }) {
  const update = useUpdatePart(part.id);
  const [f, setF] = useState<PartFormState>({
    nome: part.nome,
    tipo: part.tipo ?? 'PECA',
    sku: part.sku,
    categoria: part.categoria,
    marca: part.marca,
    partNumber: part.partNumber,
    codigoBarras: part.codigoBarras,
    quantidade: part.quantidade,
    estoqueMinimo: part.estoqueMinimo,
    localizacao: part.localizacao,
    custoUnitario: part.custoUnitario,
    fotoUrl: part.fotoUrl,
  });
  const set = (patch: PartFormState) => setF((s) => ({ ...s, ...patch }));
  const submit = async () => {
    await update.mutateAsync(f);
    onClose();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={`Editar ${part.nome}`}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={!f.nome || !f.sku || update.isPending}>
            {update.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <PartFormFields f={f} set={set} />
    </Modal>
  );
}
